import { useCustomTheme } from "@/hooks/useCustomTheme";
import { skipToken } from "@tanstack/react-query";
import { ColorScale, ParkSkinAPIObject, Theme } from "ipctg-inflow-types/InFlowAPIMisc";
import { ReactNode, useEffect, useState } from "react";
import { StyleProp, ViewStyle } from "react-native";
import Svg, { NumberProp } from "react-native-svg";

type ColorWeight =
  | ColorScale
  | {
      error: string;
      warning: string;
      success: string;
      muted: string;
      info: string;
    }
  | {
      primary: string;
      info: string;
      error: string;
    };

export interface CustomIconProps {
  children?: ReactNode;
  fill?: string;
  height?: NumberProp;
  stroke?: string;
  style?: StyleProp<ViewStyle>;
  width?: NumberProp;
}

const getThemeColor = (
  color: string,
  mode: "light" | "dark",
  skin: ParkSkinAPIObject
): string => {
  if (color.includes("-") && skin.theme[mode]) {
    const path = color.split("-");
    return skin.theme[mode][path[1] as keyof Theme]?.[path[2] as keyof ColorWeight] ?? color;
  }
  return color;
};

const CustomIcon: React.FC<CustomIconProps> = ({
  children,
  fill = "text-typography-900",
  stroke = "none",
  height = 20,
  style,
  width = 20,
}) => {
  const { mode, skin } = useCustomTheme();
  const [fillColor, setFillColor] = useState<string>(getThemeColor(fill, mode, skin));
  const [strokeColor, setStrokeColor] = useState<string>(getThemeColor(stroke, mode, skin));

  useEffect((): void => {
    setFillColor(getThemeColor(fill, mode, skin));
    setStrokeColor(getThemeColor(stroke, mode, skin));
  }, [mode, skin, fill, stroke]);

  return (
    <Svg
      fill={fillColor}
      stroke={strokeColor}
      height={height}
      style={style}
      viewBox="0 0 20 20"
      width={width}
    >
      {children}
    </Svg>
  );
};

export default CustomIcon;
